import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Text } from '@/nova/components';
import { formatAmountWithCode, isOneTimeBilling } from '../../../data/billing';
import { legalUrl } from '../../../data/legal';
import { CFG } from '../../../../../theme/configurator-tokens';
import { useConfigurator, WIZARD_STEPS } from '../../../store/configurator';
import { VoucherField, VoucherChip } from '../../../steps/finish/components/VoucherField';
import { LifetimeBadge } from '../../../steps/finish/components/LifetimeBadge';
import { MobileHeader } from '../../components/MobileHeader';
import {
  Screen,
  Scroll,
  Heading,
  HeadingTitle,
  HeadingSub,
  Summary,
  SummaryHead,
  Rule,
  TermsButton,
  TermsLabel,
  RowEl,
  RowLeft,
  RowLabel,
  RowSub,
  CodePill,
  RowRight,
  RowPrice,
  RowNote,
  BottomBar,
  TotalRow,
  TotalLabels,
  TotalLabel,
  TotalSub,
  TotalAmount,
  Cta,
} from '../MobileFinishFlow/MobileFinishFlow.styles';

const BillingRow = ({
  label,
  sublabel,
  sublabelColor = CFG.base400,
  sublabelNode,
  symbol,
  currencyCode = '',
  amount,
  note,
  discount,
  pill,
}: {
  label: string;
  sublabel?: string;
  sublabelColor?: string;
  /** A custom sublabel node (e.g. the removable voucher chip), rendered in place of `sublabel`. */
  sublabelNode?: React.ReactNode;
  symbol?: string;
  currencyCode?: string;
  amount?: number | null;
  note?: string;
  /** Render the amount as a discount: a leading `−` and the success/green tone (the voucher line). */
  discount?: boolean;
  /** Render the sublabel as a monospaced code pill (the voucher code) instead of plain text. */
  pill?: boolean;
}): React.ReactElement => (
  <RowEl>
    <RowLeft>
      <RowLabel text={label} tag="span" />
      {sublabelNode ? (
        sublabelNode
      ) : sublabel ? (
        pill ? (
          <CodePill text={sublabel} tag="span" />
        ) : (
          <RowSub text={sublabel} tag="span" $color={sublabelColor} />
        )
      ) : null}
    </RowLeft>
    {symbol != null ? (
      <RowRight>
        <RowPrice $color={discount ? CFG.success : undefined}>
          <Text text={discount ? `−${symbol}` : symbol} tag="span" />
          <Text text={amount == null ? '–' : formatAmountWithCode(amount, currencyCode)} tag="span" />
        </RowPrice>
        {note ? <RowNote text={note} tag="span" /> : null}
      </RowRight>
    ) : null}
  </RowEl>
);

/**
 * v0 mobile step 3 — Payment review. The billing summary scrolls; a sticky bottom bar holds
 * the total + "Continue to payment", which advances to the dedicated payment step. Split
 * from v3's MobileFinishFlow (which folded review + checkout into one screen).
 */
export const MobileReviewFlow = (): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Billing is derived reactively from the live session in the store (updates as the latest
  // `sessionSave` lands), so the total reflects the current selections.
  const { billing, saveNow } = useConfigurator();
  const symbol = billing?.symbol ?? '$';
  const currencyCode = billing?.currency ?? 'USD';
  const oneTime = isOneTimeBilling(billing);

  return (
    <Screen>
      <Scroll>
        <Heading>
          <HeadingTitle text="Your website is set to launch!" tag="h1" />
          <HeadingSub text="Take a moment to review your order before launch." tag="paragraph" />
        </Heading>

        <Summary>
          <SummaryHead text="Billing Summary" tag="paragraph" />
          <Rule />

          <BillingRow
            label={t('Setup Fee')}
            symbol={symbol}
            currencyCode={currencyCode}
            amount={billing?.setupFee ?? null}
            note={t('one time')}
          />
          {/* No feature charge (the free plan): the same line as the desktop billing card. */}
          {billing?.productFeatures === 0 ? <LifetimeBadge /> : null}
          {billing?.productFeatures !== 0 ? (
            <BillingRow
              label={t('Product Features')}
              sublabel={t('Cancel anytime, no extra fees')}
              sublabelColor={CFG.success}
              symbol={symbol}
              currencyCode={currencyCode}
              amount={billing?.productFeatures ?? null}
              note={t('billed monthly')}
            />
          ) : null}
          {billing?.voucherCode ? (
            <BillingRow
              label={t('Discount Voucher')}
              sublabelNode={<VoucherChip code={billing.voucherCode} />}
              symbol={symbol}
              currencyCode={currencyCode}
              amount={billing.voucherAmount}
              discount
            />
          ) : null}
          <Rule />
          <BillingRow label={t('Platform Fee')} sublabel={t('20% of monthly revenue')} />
          <VoucherField inset={8} />
        </Summary>

        <TermsButton
          as="a"
          href={legalUrl(i18n.language, 'terms-of-use')}
          target="_blank"
          rel="noopener noreferrer"
          data-ga-id="cfg-mobile-review-terms-open"
        >
          <TermsLabel text="Terms & Conditions" tag="span" />
        </TermsButton>
      </Scroll>

      <MobileHeader activeStep={2} title="" onBack={() => navigate(WIZARD_STEPS[1].path)} overlay scrim />

      <BottomBar>
        <TotalRow>
          <TotalLabels>
            {oneTime ? (
              <TotalLabel text="One time" tag="span" />
            ) : (
              <>
                <TotalLabel text="Total due today" tag="span" />
                <TotalSub text="(setup + first month)" tag="span" />
              </>
            )}
          </TotalLabels>
          <TotalAmount>
            <Text text={`${symbol}${billing ? formatAmountWithCode(billing.dueToday, currencyCode) : '–'}`} tag="span" />
          </TotalAmount>
        </TotalRow>
        <Cta
          type="button"
          onClick={() => {
            // Persist the final configuration before advancing to the payment step.
            saveNow();
            navigate(WIZARD_STEPS[3].path);
          }}
          data-ga-id="cfg-mobile-review-cta-pay"
        >
          <Text text="Continue to payment" tag="span" />
        </Cta>
      </BottomBar>
    </Screen>
  );
};

export default MobileReviewFlow;
